import { useIntl } from "react-intl";
import MiscellaneousCheckmarkCircle from "../../../../common/icons/MiscellaneousCheckmarkCircle.tsx";
import MiscellaneousBook from "../../../../common/icons/MiscellaneousBook.tsx";
import SocialHeart from "../../../../common/icons/SocialHeart.tsx";
import MiscellaneousSearch from "../../../../common/icons/MiscellaneousSearch.tsx";
import { useHomeContextUser } from "../../homeContext.ts";
import { ReactElement } from "react";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";

interface Card {
  title: string;
  description: string;
  icon: ReactElement;
  onClick?: () => void;
  href?: string;
  to?: string;
  enabled: boolean;
}

function useCardDefinitions(showRedeem: () => void) {
  const intl = useIntl();
  const brand = useBrandLoaderData();
  const user = useHomeContextUser();

  // There may be a better way to type this, but I have not found it
  const cards: Record<
    "redeem" | "myLibrary" | "search" | "favorites" | "explore",
    Card
  > = {
    redeem: {
      title: intl.formatMessage({ defaultMessage: "Redeem Code" }),
      description: intl.formatMessage({
        defaultMessage:
          "Enter the 12 or 20 character code from your instructor, bookstore or third party store to access your content.",
      }),
      icon: <MiscellaneousCheckmarkCircle />,
      onClick: showRedeem,
      enabled: brand.RedeemCode.enabled,
    },
    myLibrary: {
      title: intl.formatMessage({ defaultMessage: "My Library" }),
      description: intl.formatMessage({
        defaultMessage:
          "Access content that you’ve purchased from VitalSource.com or redeemed from your instructor or bookstore.",
      }),
      icon: <MiscellaneousBook />,
      to: "/my-library",
      enabled: true,
    },
    search: {
      title: intl.formatMessage({
        defaultMessage: "Search",
      }),
      description: intl.formatMessage({
        defaultMessage:
          "Find the title you’re looking for or search inside of your books by keyword.",
      }),
      icon: <MiscellaneousSearch />,
      to: "/search",
      enabled: brand.Search.enabled,
    },
    favorites: {
      title: intl.formatMessage({
        defaultMessage: "Favorites",
      }),
      description: intl.formatMessage({
        defaultMessage:
          "Add any title to the Favorites folder for quick access for later.",
      }),
      icon: <SocialHeart />,
      to: "/favorites",
      enabled: true,
    },
    explore: {
      title: intl.formatMessage({ defaultMessage: "Explore" }),
      description: intl.formatMessage({
        defaultMessage:
          "Discover collections of books available now, with personalized recommendations just for you!",
      }),
      icon: <MiscellaneousBook />,
      to: "/explore",
      enabled: brand.Explore.enabled && user.hasExplore,
    },
  };

  return cards;
}

export function useHomeFeatures({
  showRedeem,
  bookCount,
}: {
  showRedeem: () => void;
  bookCount: number;
}) {
  const intl = useIntl();
  const cardDefinitions = useCardDefinitions(showRedeem);

  let title = intl.formatMessage({ defaultMessage: "Get Started" });
  let subtitle = intl.formatMessage({
    defaultMessage: "New here? These are great places to start.",
  });

  if (bookCount > 0) {
    title = intl.formatMessage({
      defaultMessage: "Enhance Your Experience",
    });
    subtitle = intl.formatMessage({
      defaultMessage: "Make the most of your study session.",
    });
  }

  let cards = [
    cardDefinitions.redeem,
    cardDefinitions.explore.enabled
      ? cardDefinitions.explore
      : cardDefinitions.myLibrary,
    cardDefinitions.search,
  ];

  if (bookCount > 0) {
    cards = [
      cardDefinitions.redeem,
      cardDefinitions.explore.enabled
        ? cardDefinitions.explore
        : cardDefinitions.search,
      cardDefinitions.favorites,
    ];
  }

  return { title, subtitle, cards: cards.filter((card) => card.enabled) };
}
